import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { AppLogoIcon } from '../ui/AppLogo';
import { isStandalonePwa } from '../../utils/clientSession';

const SNOOZE_KEY = 'nmhelper_pwa_prompt_snoozed_until';
const INSTALLED_KEY = 'nmhelper_pwa_installed';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos && isSafari;
}

function readSnoozeUntil() {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    return raw ? Number(raw) : 0;
  } catch (e) {
    return 0;
  }
}

function writeSnoozeUntil(ts) {
  try {
    localStorage.setItem(SNOOZE_KEY, String(ts));
  } catch (e) {
    // ignore
  }
}

function markInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, '1');
  } catch (e) {
    // ignore
  }
}

function wasInstalled() {
  try {
    return localStorage.getItem(INSTALLED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function PwaInstallPrompt({ labels }) {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const iosSafari = useMemo(() => isIosSafari(), []);

  const shouldEverShow = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (Capacitor.isNativePlatform?.()) return false;
    if (isStandalonePwa()) return false;
    if (wasInstalled()) return false;
    return true;
  }, []);

  useEffect(() => {
    if (!shouldEverShow) return undefined;

    let promptTimer = null;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const snoozedUntil = readSnoozeUntil();
      if (Date.now() >= snoozedUntil) setOpen(true);
    };

    const onAppInstalled = () => {
      markInstalled();
      setOpen(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    if (iosSafari) {
      const snoozedUntil = readSnoozeUntil();
      if (Date.now() >= snoozedUntil) {
        promptTimer = window.setTimeout(() => setOpen(true), 800);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      if (promptTimer) window.clearTimeout(promptTimer);
    };
  }, [shouldEverShow, iosSafari]);

  const close = useCallback(() => setOpen(false), []);

  const snoozeOneDay = useCallback(() => {
    writeSnoozeUntil(Date.now() + ONE_DAY_MS);
    setOpen(false);
  }, []);

  const onInstallClick = useCallback(async () => {
    if (!deferredPrompt) {
      snoozeOneDay();
      return;
    }
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        markInstalled();
        setOpen(false);
      } else {
        snoozeOneDay();
      }
      setDeferredPrompt(null);
    } catch (e) {
      snoozeOneDay();
    }
  }, [deferredPrompt, snoozeOneDay]);

  if (!shouldEverShow) return null;

  const canPromptNatively = Boolean(deferredPrompt);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/45 px-4 pb-6 pt-10 backdrop-blur-sm sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pwa-install-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) snoozeOneDay();
          }}
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card text-text shadow-2xl"
          >
            <div className="flex items-start gap-3 border-b border-border bg-primary-light/50 px-5 py-4">
              <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-card shadow-sm">
                <AppLogoIcon className="h-7 w-7 object-contain" />
              </div>
              <div className="min-w-0">
                <h2 id="pwa-install-title" className="text-base font-semibold tracking-tight">
                  {labels.pwaInstallTitle}
                </h2>
                <p className="mt-0.5 text-xs text-muted">{labels.pwaInstallSubtitle}</p>
              </div>
            </div>

            <div className="space-y-4 px-5 py-4">
              <ul className="space-y-2 text-sm text-text">
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-0.5 text-primary">•</span>
                  <span>{labels.pwaBenefitFast}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-0.5 text-primary">•</span>
                  <span>{labels.pwaBenefitOffline}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-0.5 text-primary">•</span>
                  <span>{labels.pwaBenefitHome}</span>
                </li>
              </ul>

              {iosSafari && !canPromptNatively && (
                <div className="rounded-xl border border-border bg-surface px-3 py-2.5 text-xs leading-5 text-muted">
                  {labels.pwaIosHint}
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-border bg-surface/60 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={snoozeOneDay}
                className="rounded-lg px-3 py-2 text-xs font-medium text-muted hover:text-text"
              >
                {labels.pwaRemindLater}
              </button>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={close}
                  className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-semibold text-text hover:bg-primary-light/40"
                >
                  {labels.pwaSkip}
                </button>
                {!iosSafari && (
                  <button
                    type="button"
                    onClick={onInstallClick}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover active:scale-[0.98]"
                  >
                    {canPromptNatively ? labels.pwaInstall : labels.pwaInstallHowto}
                  </button>
                )}
                {iosSafari && (
                  <button
                    type="button"
                    onClick={close}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
                  >
                    {labels.pwaGotIt}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PwaInstallPrompt;
